import { useEffect } from "react";
import { ChevronDown, Landmark } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";

import { OrderFormData } from "../OrderForm.schema";
import { Portfolio } from "@/api/types";

type Props = { allPortfolios: Portfolio[]; hideToggle?: boolean };

export function WithdrawalAgreementSection({
  allPortfolios,
  hideToggle = false,
}: Props) {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  const enabled = useWatch({ name: "dpWithdrawalEnabled" }) as boolean;
  const modelType = useWatch({ name: "dpModelType" }) as string | undefined;
  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const dpAccount = useWatch({ name: "dpAccount" }) as string;
  const hasModel = !!modelType && modelType !== "";

  // When the section is enabled, pre-select the same account as Account & Withdrawal
  useEffect(() => {
    if (enabled && dpAccount) {
      setValue("dpWdAccount", dpAccount, { shouldValidate: false });
    }
  }, [enabled, dpAccount, setValue]);

  // Collect KAR-OK accounts from the currently selected portfolio first,
  // then fall back to all portfolios (same pattern as AccountSection)
  const selectedPortfolio = allPortfolios.find(
    (p) => p.id === Number(portfolioId)
  );
  const seen = new Set<string>();
  const accountNumbers: string[] = [];
  // Prioritise selected portfolio's accounts
  if (selectedPortfolio) {
    for (const a of selectedPortfolio.accounts) {
      if (a.tagsAsSet.includes("KAR-OK") && !seen.has(a.number)) {
        seen.add(a.number);
        accountNumbers.push(a.number);
      }
    }
  }
  // Then add remaining accounts from other portfolios
  for (const p of allPortfolios) {
    for (const a of p.accounts) {
      if (a.tagsAsSet.includes("KAR-OK") && !seen.has(a.number)) {
        seen.add(a.number);
        accountNumbers.push(a.number);
      }
    }
  }

  const toggle = () => {
    const next = !enabled;
    setValue("dpWithdrawalEnabled", next, { shouldValidate: true });
    if (!next) {
      setValue("dpWdAccount", "");
      setValue("dpWdDayOfMonth", undefined);
      setValue("dpWdAmount", undefined);
    }
  };

  return (
    <div className="mt-2">
      {/* Toggle header — hidden when used inside AgreementsGroup */}
      {!hideToggle && (
        <button
          type="button"
          onClick={toggle}
          disabled={!hasModel}
          title={
            !hasModel
              ? "Velg en modellportefølje for å aktivere uttaksavtale"
              : undefined
          }
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors
            ${
              !hasModel
                ? "border-dashed border-gray-200 bg-transparent text-gray-300 cursor-not-allowed dark:border-gray-700 dark:text-gray-600"
                : enabled
                ? "border-blue-400 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                : "border-dashed border-gray-300 bg-transparent text-gray-500 hover:border-gray-400 hover:text-gray-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500"
            }`}
        >
          <span className="flex items-center gap-2">
            <Landmark className="h-4 w-4 flex-shrink-0" />
            Uttaksavtale
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${
              enabled ? "rotate-180" : ""
            }`}
          />
        </button>
      )}

      {/* Collapsible body */}
      {enabled && (
        <div
          className={`border border-blue-200 bg-blue-50/40 dark:border-blue-900/60 dark:bg-blue-950/10 space-y-3 ${
            hideToggle
              ? "rounded-lg px-4 pt-2 pb-4"
              : "mt-1 rounded-b-lg border-t-0 px-4 py-4"
          }`}
        >
          {/* Section label — only shown when toggle is external */}
          {hideToggle && (
            <div className="flex items-center gap-2 py-2 border-b border-blue-200 dark:border-blue-900/60 mb-1">
              <Landmark className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                Uttaksavtale
              </span>
            </div>
          )}
          <div className="grid grid-cols-[180px_1fr] items-center gap-3">
            <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
              Konto:
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex flex-col gap-1">
              <select
                {...register("dpWdAccount")}
                className={`w-full rounded-md border bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.dpWdAccount
                    ? "border-red-400 dark:border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                <option value="">-- Velg konto --</option>
                {accountNumbers.map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              {errors.dpWdAccount && (
                <p className="text-xs text-red-500">
                  {errors.dpWdAccount.message}
                </p>
              )}
            </div>
          </div>

          {/* Uttaksdato */}
          <div className="grid grid-cols-[180px_1fr] items-center gap-3">
            <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
              Withdrawal date:
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={31}
                  step={1}
                  placeholder="1"
                  {...register("dpWdDayOfMonth", { valueAsNumber: true })}
                  className={`w-20 rounded-md border bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.dpWdDayOfMonth
                      ? "border-red-400 dark:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  dag i måneden
                </span>
              </div>
              {errors.dpWdDayOfMonth && (
                <p className="text-xs text-red-500">
                  {errors.dpWdDayOfMonth.message}
                </p>
              )}
            </div>
          </div>

          {/* Uttaksbeløp */}
          <div className="grid grid-cols-[180px_1fr] items-center gap-3">
            <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
              Withdrawal amount:
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex flex-col gap-1">
              <div className="relative w-full">
                <input
                  type="number"
                  min={0}
                  step={100}
                  placeholder="0"
                  {...register("dpWdAmount", { valueAsNumber: true })}
                  className={`w-full rounded-md border bg-white dark:bg-gray-800 px-3 py-1.5 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.dpWdAmount
                      ? "border-red-400 dark:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                  kr
                </span>
              </div>
              {errors.dpWdAmount && (
                <p className="text-xs text-red-500">
                  {errors.dpWdAmount.message}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
